import React, { useContext } from 'react'
import Link from 'next/link'
import SVG from './SVG'
import Context from '../context'
import { useChangeCartStore, useAddToCart } from '../hooks/cart.hooks'

interface StoreListItemProps {
  store: any
  selectedStoreId?: string
  showActions?: boolean
  sku?: string | null
  cartAddData?: any
  handleDismiss?: () => void
  showDistanceLabel?: boolean
}

const StoreListItem: React.FC<StoreListItemProps> = ({
  store,
  selectedStoreId = '',
  showActions = true,
  sku = null,
  cartAddData = {},
  handleDismiss = () => {},
  showDistanceLabel = true
}) => {
  const { state } = useContext(Context)
  const changeCartCall = useChangeCartStore()
  const makeAddToCartCall = useAddToCart()

  const getClosingTimeText = (store) => {
    const dayMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
    const hoursProp = `hours_${dayMap[new Date().getDay()]}`
    const hours = store[hoursProp]
    if (!hours) return null
    const closing = hours.split(':')[1]
    if (!closing || closing.length !== 4) return null
    const hour = parseInt(closing.substring(0, 2), 10)
    const minute = parseInt(closing.substring(2, 4), 10)
    const hour12 = hour % 12 || 12
    const ampm = hour >= 12 ? 'pm' : 'am'
    const minuteStr = minute > 0 ? `:${minute.toString().padStart(2, '0')}` : ''
    return `Closes at ${hour12}${minuteStr}${ampm} today`
  }

  const setStore = async (store) => {
    if (Object.keys(cartAddData).length > 0) {
      // @ts-ignore
      await makeAddToCartCall(store.storenumber, cartAddData.upc, cartAddData.digitalSku, cartAddData.sku, 1, true)
    } else if (Object.keys(cartAddData).length === 0) {
      await changeCartCall(store.storenumber)
    }
    handleDismiss()
  }

  const setCurrentStoreLink = (
    <button
      className="store-list-item__set-current-store-link"
      disabled={sku && (store?.inventory <= 0 || store?.inventory === -1)}
      onClick={(e) => {
        e.preventDefault()
        setStore(store)
      }}
    >
      Set as current store
    </button>
  )

  const yourStoreBadge = (
    <div className="store-list-item__your-store-badge">
      <SVG name="checkmark-white" label="checkmark-white" size="base" />
      <span>your store</span>
    </div>
  )

  const previewText = <label className="store-list-item__message-text">coming soon!</label>
  const closedText = <label className="store-list-item__message-text">temporarily closed</label>
  const pegasusText = <label className="store-list-item__message-text">located inside a DG Market store</label>

  let changeStoreAction = store.storenumber === state.cart.store?.storenumber ? yourStoreBadge : setCurrentStoreLink

  if (store.store_service_names?.includes('DGMarketPopShelf')) {
    changeStoreAction = pegasusText
  } else if (store.store_preview) {
    changeStoreAction = previewText
  } else if (store.is_closed) {
    changeStoreAction = closedText
  }

  return (
    <div className={`store-list-item ${selectedStoreId === store.storenumber ? 'store-list-item--chosen' : ''}`} key={store.storenumber}>
      <div className="store-list-item__content">
        <div className="store-list-item__content__label">{`${store.city}, ${store.state}`}</div>
        <div className="store-list-item__content__address" title={store.address}>{`${store.address}`}</div>
        {(store.distance || showDistanceLabel) && (
          <div className="store-list-item__content__address-distance">
            <SVG name="icon-store-location" label="store location" size="base" />
            {store.distance ? `${store.distance.toFixed(2)} miles away` : 'Search by zipcode'}
          </div>
        )}
        {sku && store.inventory > 0 &&
          <div className="store-list-item__content-eligible">
            <SVG name="checkmark-green" label="checkmark-green" size="base" />
            <p>in stock</p>
          </div>
        }
        {sku && store.inventory === -1 && (
          <p className="store-list-item__content-ineligible">Not Eligible for pickup</p>
        )}
        {sku && (store.inventory === 0 || store.inventory === null) && (
          <p className="store-list-item__content-oos">Out of stock</p>
        )}
        {!sku && (
          <div className="store-list-item__closing-time">
            <SVG name="clock" label="clock" size="base" />
            <span className="store-list-item__closing-time-text">
              {getClosingTimeText(store)}
            </span>
          </div>
        )}
      </div>
      <div className="store-list-item__content__btn-select">
        <Link href={`/store/${store.storenumber}`} aria-label={'view store details ' + store.city + ' ' + store.state + ' ' + store.address}
            className="btn__secondary store-search__select-btn"
            style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            STORE DETAILS
          </Link>
        {showActions && changeStoreAction}
      </div>
    </div>
  )
}

export default StoreListItem
